import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Content from "../../layout/content/Content";
import {
  Card,
  Row,
  Col,
  Button,
  Spinner,
  Alert,
  Badge,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import Head from "../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
} from "../../components/Component";
import {
  Table,
  Collapse as AntCollapse,
  TableColumnsType,
  Descriptions,
  Modal,
  Timeline,
  Popover,
  Empty,
  Flex,
  Spin,
} from "antd";
import {
  PaperClipOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import WorkBoqAside from "./workBoqAside";
import { formatDecimal } from "../../utils/Utils";
import WorkBoqPdfGenerator from "./WorkBoqPdfGenerator";
import { useSelector } from "react-redux";

const { Panel } = AntCollapse;

interface WorkBoqViewData {
  id: string;
  boqTitle: string;
  boqDescription: string;
  siteName: string;
  projectName?: string;
  contractorName?: string;
  workLevel: string;
  materialBoqName: string;
  completionAlertsFrom?: string;
  status: "Draft" | "Pending Approval" | "Approved" | "Rejected";
  createdAt: string;
  createdBy: string;
  submittedAt?: string;
  submittedBy?: string;
  approvals?: any[];
  rejectedReason?: string;
  workOrderLines: any[];
  instructions?: string;
  attachments?: any;
}

const WorkBoqView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: currentUser } = useSelector((state: any) => state.currentUser);
  const [workBoqData, setWorkBoqData] = useState<WorkBoqViewData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sm, setSm] = useState(false);
  const [tab, setTab] = useState("1");
  const [screenSize, setScreenSize] = useState(0);
  const [mobileView, setMobileView] = useState(false);
  const [disableApprove, setDisableApprove] = useState(false);
  const [disableReject, setDisableReject] = useState(false);
  const [shareOptionsToggle, setShareOptionsToggle] = useState(false);
  const [instructionsVisible, setInstructionsVisible] = useState(false);

  // Mock current user permissions
  const [allowedActions] = useState({
    view: 1,
    edit: 1,
    approve: 1,
    reject: 1,
  });

  const [currentUserId] = useState(1); // Mock current user ID
  const [approverIds] = useState([1, 2, 3]); // Mock approver IDs

  useEffect(() => {
    fetchWorkBoqDetails();
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    const headerClick = () => setSm(false);
    document
      .getElementsByClassName("nk-header")[0]
      ?.addEventListener("click", headerClick);

    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
      document
        .getElementsByClassName("nk-header")[0]
        ?.removeEventListener("click", headerClick);
    };
  }, [id]);

  const viewChange = () => {
    setScreenSize(window.innerWidth);
    if (window.innerWidth < 990) {
      setMobileView(true);
    } else {
      setMobileView(false);
      setSm(false);
    }
  };

  const fetchWorkBoqDetails = async () => {
    try {
      // Mock data - replace with actual API call
      const mockData: WorkBoqViewData = {
        id: id || "WB002",
        boqTitle: "Plumbing Infrastructure - Phase 1",
        boqDescription:
          "Complete plumbing infrastructure work for commercial building including water supply, drainage, and sanitary installations.",
        siteName: "Commercial Building B",
        projectName: "Commercial Complex Development",
        contractorName: "XYZ Plumbing Co",
        workLevel: "Project",
        materialBoqName: "Plumbing Materials BOQ #003",
        completionAlertsFrom: "25",
        status: "Pending Approval",
        createdAt: "2025-01-09T14:15:00Z",
        createdBy: "Jane Doe",
        submittedAt: "2025-01-09T16:30:00Z",
        submittedBy: "Jane Doe",
        instructions:
          "Please ensure all plumbing work follows the building codes and safety standards. Coordinate with electrical team for proper routing.",
        attachments: [
          {
            id: 1,
            name: "plumbing-specifications.pdf",
            link: "/docs/plumbing-specs.pdf",
          },
          {
            id: 2,
            name: "building-layout.dwg",
            link: "/docs/building-layout.dwg",
          },
        ],
        approvals: [
          {
            id: 1,
            userName: "John Manager",
            roleName: "Site Manager",
            status: "Approved",
            approvalDate: "2025-01-09T17:00:00Z",
          },
          {
            id: 2,
            userName: "Sarah Director",
            roleName: "Project Director",
            status: "Pending",
            approvalDate: null,
          },
        ],
        workOrderLines: [
          {
            id: "1",
            workCategory: "Plumbing",
            workSubCategory: "Water Supply",
            taskTitle: "Main Water Line Installation",
            taskDescription:
              "Install main water supply line from ground floor to all floors",
            quantity: 150,
            uom: "m",
            towerVilla: "Main Building",
            allocatedMaterials: [
              { materialName: "PVC Pipes 4 inch" },
              { materialName: "Water Fittings" },
              { materialName: "Valves" },
            ],
            uploadedDocs: [{ name: "water-line-drawing.pdf" }],
          },
          {
            id: "2",
            workCategory: "Plumbing",
            workSubCategory: "Drainage",
            taskTitle: "Sewage Pipe Setup",
            taskDescription:
              "Install sewage drainage system for entire building",
            quantity: 200,
            uom: "m",
            towerVilla: "Main Building",
            allocatedMaterials: [
              { materialName: "Sewage Pipes" },
              { materialName: "Manholes" },
              { materialName: "Drainage Fittings" },
            ],
            uploadedDocs: [],
          },

          {
            id: "1",
            workCategory: "Plumbing",
            workSubCategory: "Water Supply",
            taskTitle: "Main Water Line Installation",
            taskDescription:
              "Install main water supply line from ground floor to all floors",
            quantity: 150,
            uom: "m",
            towerVilla: "Main Building",
            allocatedMaterials: [
              { materialName: "PVC Pipes 4 inch" },
              { materialName: "Water Fittings" },
              { materialName: "Valves" },
            ],
            uploadedDocs: [{ name: "water-line-drawing.pdf" }],
          },
          {
            id: "2",
            workCategory: "Plumbing",
            workSubCategory: "Drainage",
            taskTitle: "Sewage Pipe Setup",
            taskDescription:
              "Install sewage drainage system for entire building",
            quantity: 200,
            uom: "m",
            towerVilla: "Main Building",
            allocatedMaterials: [
              { materialName: "Sewage Pipes" },
              { materialName: "Manholes" },
              { materialName: "Drainage Fittings" },
            ],
            uploadedDocs: [],
          },
          {
            id: "1",
            workCategory: "Plumbing",
            workSubCategory: "Water Supply",
            taskTitle: "Main Water Line Installation",
            taskDescription:
              "Install main water supply line from ground floor to all floors",
            quantity: 150,
            uom: "m",
            towerVilla: "Main Building",
            allocatedMaterials: [
              { materialName: "PVC Pipes 4 inch" },
              { materialName: "Water Fittings" },
              { materialName: "Valves" },
            ],
            uploadedDocs: [{ name: "water-line-drawing.pdf" }],
          },
          {
            id: "2",
            workCategory: "Plumbing",
            workSubCategory: "Drainage",
            taskTitle: "Sewage Pipe Setup",
            taskDescription:
              "Install sewage drainage system for entire building",
            quantity: 200,
            uom: "m",
            towerVilla: "Main Building",
            allocatedMaterials: [
              { materialName: "Sewage Pipes" },
              { materialName: "Manholes" },
              { materialName: "Drainage Fittings" },
            ],
            uploadedDocs: [],
          },
        ],
      };
      setWorkBoqData(mockData);
    } catch (error) {
      console.error("Error fetching Work BOQ details:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch Work BOQ details",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Work order lines table columns
  const [columns] = useState<TableColumnsType<any>>([
    // {
    //   ...Table.EXPAND_COLUMN,
    //   fixed: "left",
    // },
    {
      title: "#",
      key: "#",
      width: 50,
      fixed: "left",
      render: (text, record, index) => index + 1,
    },
    {
      title: "Work Category",
      dataIndex: "workCategory",
      key: "workCategory",
      width: 150,
      fixed: "left",
    },
    {
      title: "Sub Category",
      dataIndex: "workSubCategory",
      key: "workSubCategory",
      width: 150,
      render: (text) => text || "-",
    },
    {
      title: "Task Title",
      dataIndex: "taskTitle",
      key: "taskTitle",
      width: 200,
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
      width: 100,
      render: (text, record) => `${formatDecimal(text)} ${record?.uom}`,
    },
    {
      title: "UOM",
      dataIndex: "uom",
      key: "uom",
      width: 80,
    },
    {
      title: "Location",
      key: "location",
      width: 150,
      hidden: workBoqData?.workLevel === "Project",
      render: (text, record) => {
        const location = [];
        if (record.towerVilla) location.push(record.towerVilla);
        if (record.floorName) location.push(record.floorName);
        if (record.flatName) location.push(record.flatName);
        return location.join(" / ") || "-";
      },
    },
    {
      title: "Materials",
      key: "materials",
      width: 120,
      render: (text, record) => (
        <Badge color="outline-info">
          {record.allocatedMaterials?.length || 0} items
        </Badge>
      ),
    },
    {
      title: "Documents",
      key: "documents",
      width: 100,
      render: (text, record) =>
        record.uploadedDocs?.length > 0 ? (
          <CheckCircleOutlined style={{ color: "#28a745" }} />
        ) : (
          <CloseCircleOutlined style={{ color: "#dc3545" }} />
        ),
    },
  ]);

  const handleApprove = async () => {
    try {
      setDisableApprove(true);
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      setWorkBoqData((prev) =>
        prev ? { ...prev, status: "Approved" as const } : null
      );

      Swal.fire(
        "Approved!",
        "Work BOQ has been approved successfully",
        "success"
      ).then(() => {
        navigate("/work-boq-list");
      });
    } catch (error) {
      console.error(error);
      Swal.fire("Error!", "Failed to approve Work BOQ", "error");
    } finally {
      setDisableApprove(false);
    }
  };

  const handleReject = async (rejectedReason: string) => {
    try {
      setDisableReject(true);
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));

      setWorkBoqData((prev) =>
        prev
          ? {
              ...prev,
              status: "Rejected" as const,
              rejectedReason,
            }
          : null
      );

      Swal.fire("Rejected!", "Work BOQ has been rejected", "success").then(
        () => {
          navigate("/work-boq-list");
        }
      );
    } catch (error) {
      console.error(error);
      Swal.fire("Error!", "Failed to reject Work BOQ", "error");
    } finally {
      setDisableReject(false);
    }
  };

  const showRejectModal = () => {
    Swal.fire({
      title: "Reject Work BOQ",
      html: `
        <textarea 
          id="rejection-reason" 
          placeholder="Enter rejection reason (required)"
          style="width: 100%; height: 100px; margin-top: 10px; padding: 8px; border: 1px solid #ddd; border-radius: 4px;"
        ></textarea>
      `,
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Reject",
      cancelButtonText: "Cancel",
      preConfirm: () => {
        const reason = (
          document.getElementById("rejection-reason") as HTMLTextAreaElement
        )?.value;
        if (!reason.trim()) {
          Swal.showValidationMessage("Rejection reason is required");
          return false;
        }
        return reason;
      },
    }).then((result: any) => {
      if (result.isConfirmed) {
        setDisableReject(true);
        handleReject(result.value);
      }
    });
  };

  const ApprovalTimelineContent = () => {
    const approvalsData = workBoqData?.approvals;

    if (!approvalsData || approvalsData?.length === 0) {
      return <Badge color="outline-danger">No</Badge>;
    }

    return (
      <Popover
        content={
          <Timeline className="approval-timeline">
            {approvalsData?.map((approval: any, index: number) => {
              const isApproved = approval.status === "Approved";
              const isPending = approval.status === "Pending";
              return (
                <Timeline.Item
                  key={index}
                  dot={
                    isApproved ? (
                      <CheckCircleOutlined style={{ color: "green" }} />
                    ) : isPending ? (
                      <CloseCircleOutlined style={{ color: "orange" }} />
                    ) : (
                      <CloseCircleOutlined style={{ color: "red" }} />
                    )
                  }
                >
                  {approval?.userName}
                  <div className="designation">{approval?.roleName}</div>
                  {approval?.approvalDate && (
                    <div className="designation">
                      {dayjs(approval?.approvalDate).format("DD-MMM-YYYY")}
                    </div>
                  )}
                </Timeline.Item>
              );
            })}
          </Timeline>
        }
        title={<span style={{ color: "#2263b3" }}>Approval Hierarchy</span>}
      >
        <span
          style={{
            color: "#2263b3",
            textDecoration: "underline",
            cursor: "pointer",
          }}
        >
          <Badge color="outline-success">Yes</Badge>
        </span>
      </Popover>
    );
  };

  const getStatusMapper = (status: string) => {
    const statusMap: { [key: string]: { title: string; color: string } } = {
      Draft: { title: "Draft", color: "secondary" },
      "Pending Approval": { title: "Pending Approval", color: "warning" },
      Approved: { title: "Approved", color: "success" },
      Rejected: { title: "Rejected", color: "danger" },
    };
    return statusMap[status] || { title: status, color: "secondary" };
  };

  if (isLoading) {
    return (
      <>
        <Head title="Work BOQ Details" />
        <Content>
          <Flex align="center" gap="middle" className="pageLoader">
            <Spin size="large" className="m-auto" />
          </Flex>
        </Content>
      </>
    );
  }

  if (!workBoqData) {
    return (
      <>
        <Head title="Work BOQ Not Found" />
        <Content>
          <div className="text-center py-5">
            <Empty description="Work BOQ not found" />
          </div>
        </Content>
      </>
    );
  }

  const statusInfo = getStatusMapper(workBoqData.status);

  return (
    <>
      <Head title={`Work BOQ - ${workBoqData.boqTitle}`} />
      <Content>
        <Card className="card-bordered">
          <div className="card-aside-wrap">
            <div className="card-inner card-inner-lg">
              {sm && mobileView && (
                <div
                  className="toggle-overlay"
                  onClick={() => setSm(!sm)}
                ></div>
              )}
              <BlockHead size="lg">
                <BlockBetween>
                  <BlockHeadContent>
                    <BlockTitle tag="h4">
                      <span>
                        {workBoqData.id} | {statusInfo.title}
                      </span>
                    </BlockTitle>
                  </BlockHeadContent>
                  <div className="d-flex">
                    {["Pending Approval"].includes(workBoqData.status) &&
                      allowedActions?.approve === 1 &&
                      approverIds.includes(currentUserId) && (
                        <div className="actions clearfix">
                          <ul
                            className="m-auto d-flex align-items-center justify-content-center p-0"
                            style={{
                              flexDirection: "row-reverse",
                            }}
                          >
                            <li className="pt-0 pb-0">
                              {disableApprove ? (
                                <Button color="success" type="button">
                                  <Spinner size="sm" color="light" />
                                </Button>
                              ) : (
                                <Button
                                  color="success"
                                  type="button"
                                  onClick={() => {
                                    Swal.fire({
                                      title: "Are you sure?",
                                      text: "Do you want to approve this Work BOQ?",
                                      icon: "warning",
                                      showCancelButton: true,
                                      confirmButtonText: "Yes, Approve it!",
                                      cancelButtonText: "No, keep it",
                                    }).then((result) => {
                                      if (result.isConfirmed) {
                                        handleApprove();
                                      }
                                    });
                                  }}
                                >
                                  Approve
                                </Button>
                              )}
                            </li>
                            <li className="pt-0 pb-0">
                              {disableReject ? (
                                <Button color="danger" type="button">
                                  <Spinner size="sm" color="light" />
                                </Button>
                              ) : (
                                <Button
                                  color="danger"
                                  onClick={showRejectModal}
                                  type="button"
                                  className="btn-dim btn btn-secondary"
                                >
                                  Reject
                                </Button>
                              )}
                            </li>
                          </ul>
                        </div>
                      )}

                    <div className="actions clearfix">
                      <ul
                        className="m-auto d-flex align-items-center justify-content-center p-0"
                        style={{
                          flexDirection: "row-reverse",
                        }}
                      >
                        <li className="pt-0 pb-0">
                          <Dropdown
                            isOpen={shareOptionsToggle}
                            toggle={() => {
                              setShareOptionsToggle(!shareOptionsToggle);
                            }}
                          >
                            <DropdownToggle className="btn btn-white btn-outline-light">
                              <Icon name="share"></Icon>
                              <span>Share</span>
                            </DropdownToggle>
                            <DropdownMenu>
                              <DropdownItem onClick={() => {}}>
                                <Icon name="mail"></Icon>
                                <span>Send Mail</span>
                              </DropdownItem>
                              {/* <DropdownItem onClick={() => {}}>
                                <Icon name="download"></Icon>
                                <span>Export PDF</span>
                              </DropdownItem> */}
                            </DropdownMenu>
                          </Dropdown>
                        </li>
                        <li className="pt-0 pb-0">
                          <WorkBoqPdfGenerator
                            workBoqData={workBoqData}
                            orgLogo={currentUser?.orgLogo}
                            orgName={currentUser?.orgName}
                            getPdfUrl={() => {}}
                          />
                        </li>

                        <li className="pt-0 pb-0">
                          <Button
                            className="toggle"
                            color="primary"
                            onClick={() => {
                              navigate("/work-boq-list");
                            }}
                          >
                            <Icon name="arrow-left" />
                            <span>Back</span>
                          </Button>
                        </li>
                      </ul>
                    </div>
                  </div>
                  <BlockHeadContent className="align-self-start d-lg-none">
                    <Button
                      className={`toggle btn btn-icon btn-trigger mt-n1 ${
                        sm ? "active" : ""
                      }`}
                      onClick={() => setSm(!sm)}
                    >
                      <Icon name="menu-alt-r" />
                    </Button>
                  </BlockHeadContent>
                </BlockBetween>
              </BlockHead>

              <Alert
                color={"danger"}
                isOpen={workBoqData.status === "Rejected"}
              >
                <strong>Rejected: </strong>
                {workBoqData.rejectedReason || "No reason provided"}
              </Alert>

              <div className="content clearfix">
                <AntCollapse defaultActiveKey={["1", "2", "3"]}>
                  <Panel
                    header={
                      <div className="d-flex align-items-center justify-content-between">
                        <>General Information</>
                      </div>
                    }
                    key="1"
                  >
                    <Col className="justify-start w-100 flex-wrap mt-3 p-3">
                      <div className="w-30 border-end">
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">BOQ ID</div>
                          <div className="data-value w-70">
                            {workBoqData.id || "--"}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Site</div>
                          <div className="data-value w-70">
                            {workBoqData.siteName || "--"}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Project</div>
                          <div className="data-value w-70">
                            {workBoqData.projectName || "--"}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Approvals</div>
                          <div className="data-value w-70">
                            <ApprovalTimelineContent />
                          </div>
                        </div>
                      </div>
                      <div className="w-30 border-end paddingLeft10">
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Contractor</div>
                          <div className="data-value w-70">
                            {workBoqData.contractorName || "--"}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Material BOQ</div>
                          <div className="data-value w-70">
                            {workBoqData.materialBoqName || "--"}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Work Level</div>
                          <div className="data-value w-70">
                            <Badge color="outline-primary">
                              {workBoqData.workLevel || "--"}
                            </Badge>
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Status</div>
                          <div className="data-value w-70">
                            <Badge color={`outline-${statusInfo.color}`}>
                              {statusInfo.title}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <div className="w-30 paddingLeft10">
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Description</div>
                          <div className="data-value w-70">
                            <Popover
                              content={
                                <div>
                                  <p>{workBoqData.boqDescription}</p>
                                </div>
                              }
                              title={"BOQ Description"}
                            >
                              {workBoqData.boqDescription?.length > 30 ? (
                                <span
                                  style={{
                                    color: "#2263b3",
                                    textDecoration: "underline",
                                    cursor: "pointer",
                                  }}
                                >
                                  {workBoqData.boqDescription.substring(0, 30)}
                                  ...
                                </span>
                              ) : (
                                workBoqData.boqDescription || "--"
                              )}
                            </Popover>
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">
                            Completion Alert
                          </div>
                          <div className="data-value w-70">
                            <Badge color="outline-info">
                              {workBoqData.completionAlertsFrom
                                ? `${workBoqData.completionAlertsFrom}%`
                                : "Not Set"}
                            </Badge>
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Created By</div>
                          <div className="data-value w-70">
                            {workBoqData.createdBy || "--"}
                          </div>
                        </div>
                        <div className="data-col align-center pt-1">
                          <div className="data-label w-30">Created At</div>
                          <div className="data-value w-70">
                            {dayjs(workBoqData.createdAt).format("DD-MMM-YYYY")}
                          </div>
                        </div>
                      </div>
                    </Col>
                  </Panel>

                  <Panel header="Work Order Lines" key="2">
                    <div className="table-responsive poTable">
                      <div
                        style={{
                          width: screenSize - 170 + "px",
                          margin: "auto",
                        }}
                      >
                        {!workBoqData.workOrderLines ||
                        workBoqData.workOrderLines.length === 0 ? (
                          <div className="text-center py-5">
                            <Empty description="No work order lines found" />
                          </div>
                        ) : (
                          <Table
                            className="customTable"
                            columns={columns}
                            dataSource={workBoqData.workOrderLines}
                            scroll={{ x: 1000 }}
                            expandable={{
                              expandedRowRender: (record: any) => (
                                <div className="expandableBody">
                                  <div style={{ flex: 1, textAlign: "left" }}>
                                    {record.taskDescription && (
                                      <div className="mb-3">
                                        <strong>Task Description:</strong>
                                        <p style={{ margin: "5px 0" }}>
                                          {record.taskDescription}
                                        </p>
                                      </div>
                                    )}

                                    {record.allocatedMaterials?.length > 0 && (
                                      <div className="mb-3">
                                        <strong>Allocated Materials:</strong>
                                        <ul
                                          style={{
                                            margin: "5px 0",
                                            paddingLeft: "20px",
                                          }}
                                        >
                                          {record.allocatedMaterials.map(
                                            (material: any, index: number) => (
                                              <li key={index}>
                                                {material.materialName ||
                                                  material.name}
                                              </li>
                                            )
                                          )}
                                        </ul>
                                      </div>
                                    )}

                                    {record.uploadedDocs?.length > 0 && (
                                      <div>
                                        <strong>Documents:</strong>
                                        {record.uploadedDocs.map(
                                          (doc: any, index: number) => (
                                            <div
                                              key={index}
                                              style={{ marginLeft: "10px" }}
                                            >
                                              <PaperClipOutlined /> &nbsp;
                                              {doc.name}
                                            </div>
                                          )
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ),
                            }}
                            bordered
                            size="small"
                            pagination={false}
                          />
                        )}
                      </div>

                      {workBoqData.workOrderLines?.length > 0 && (
                        <Row style={{ margin: "20px" }}>
                          <Col className="justify-end">
                            <div className="w-40">
                              <div className="d-flex justify-content-between">
                                <div className="w-50 justify-end">
                                  <strong>Total Work Lines</strong>
                                </div>
                                <span className="r_align">
                                  {workBoqData.workOrderLines.length}
                                </span>
                              </div>
                              <div className="d-flex justify-content-between">
                                <div className="w-50 justify-end">
                                  <strong>Total Quantity</strong>
                                </div>
                                <span className="r_align">
                                  {formatDecimal(
                                    workBoqData.workOrderLines.reduce(
                                      (total: number, line: any) =>
                                        total + (Number(line.quantity) || 0),
                                      0
                                    )
                                  )}
                                </span>
                              </div>
                              <div className="d-flex justify-content-between">
                                <div className="w-50 justify-end">
                                  <strong>Work Categories</strong>
                                </div>
                                <span className="r_align">
                                  {
                                    new Set(
                                      workBoqData.workOrderLines.map(
                                        (line: any) => line.workCategory
                                      )
                                    ).size
                                  }
                                </span>
                              </div>
                            </div>
                          </Col>
                        </Row>
                      )}
                    </div>
                  </Panel>
                </AntCollapse>
              </div>
            </div>
          </div>
        </Card>
      </Content>

      <Modal
        title="Instructions"
        visible={instructionsVisible}
        footer={null}
        onCancel={() => setInstructionsVisible(false)}
        width="60%"
        bodyStyle={{ maxHeight: "70vh", overflowY: "auto" }}
      >
        {workBoqData.instructions ? (
          <pre>{workBoqData.instructions}</pre>
        ) : (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "200px",
            }}
          >
            <Empty description="No instructions provided for this work BOQ" />
          </div>
        )}
      </Modal>
    </>
  );
};

export default WorkBoqView;
